<template>
    <div id="contactForm">
        <p class="paragraph font-bold">Message us:</p>

        <div class="flex flex-wrap">
            <div class="w-full  border border-gray-200">
                <div class="p-6">
                        <div class="w-full mb-6">
                            <label class="form-label-class" for="name">Name</label>
                            <input @input="errors.name = false" :class="{'is-invalid': errors.name}" v-model="form.name" class="form-input" id="name" type="text" placeholder="">
                            <div class="error" v-if="errors.name"> {{ errors.name }}</div>
                        </div>
                        <div class="w-full mb-6">
                            <label class="form-label-class" for="email">Email</label>
                            <input @input="errors.email = false" :class="{'is-invalid': errors.email}" v-model="form.email" class="form-input" id="email" type="email" placeholder="">
                            <div class="error" v-if="errors.email"> {{ errors.email }}</div>
                        </div>
                        <div class="w-full mb-6">
                            <label class="form-label-class" for="notes">How can we help you?</label>
                            <textarea @input="errors.message = false" :class="{'is-invalid': errors.message}" v-model="form.message" class="form-input" id="notes" type="text" rows="3" placeholder=""></textarea>
                            <div class="error" v-if="errors.message"> {{ errors.message }}</div>
                        </div>

                        <div class="w-full mb-6" v-if="!loggedIn">
                            <div id="captcha"></div>
                            <div class="error" v-if="errors.captcha">The reCAPTCHA was not entered correctly</div>
                        </div>

                        <div class="w-full mt-2 text-center">
                            <button :class="{'btn-disabled' : loading}" class="btn btn-yellow" @click="submit" type="button">
                                Submit Message
                            </button>
                        </div>
                </div>
            </div>
        </div>

        <flash-message ref="flash" :seconds-to-display="6"></flash-message>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    siteKey: String,
    previousPage: String,
});

const flash = ref(null);
const errors = ref({
    name: false,
    email: false,
    message: false,
    captcha: false
});
const form = ref({
    name: '',
    email: (window.currentUser && window.currentUser.email) || '',
    message: '',
    captcha: ''
});
const loggedIn = ref((window.currentUser && window.currentUser.username) ? true : false);
const displayEmail = ref('');
const loading = ref(false);

const validate = () => {
    let valid = true;
    if (loggedIn.value) {
        form.value.captcha = true;
    }

    Object.keys(form.value).forEach((formName) => {
        if (form.value[formName] === '') {
            errors.value[formName] = 'Please fill this field';
            valid = false;
        }
    });

    return valid;
};

const updateCaptcha = (response) => {
    form.value.captcha = response;
};

const reset = () => {
    Object.keys(form.value).forEach((formName) => {
        form.value[formName] = '';
    });

    if (!loggedIn.value) {
        grecaptcha.reset();
    }
};

const submit = () => {
    const route = loggedIn.value
        ? window.route('main.contact-us-logged-in')
        : window.route('post.contact-us');

    if (!validate()) return;

    loading.value = true;

    const params = {
        ...form.value,
        page: props.previousPage,
        user: window.currentUser,
        resolution: {
            height: window.innerHeight,
            width: window.innerWidth
        }
    };

    axios.post(route, params)
        .then((response) => {
            displayEmail.value = form.value.email;
            flash.value.display('Your message has been sent! A response will be sent to ' + form.value.email + ' shortly.');
            reset();
        })
        .catch((error) => {
            if (error.response.status === 500) {
                displayEmail.value = form.value.email;
                flash.value.error('Something went wrong, please try again later');
            } else {
                if (error.response.data.errors && typeof error.response.data.errors === 'object') {
                    Object.keys(error.response.data.errors).forEach((formName) => {
                        errors.value[formName] = error.response.data.errors[formName][0];
                    });
                } else {
                    displayEmail.value = form.value.email;
                    flash.value.error('Something went wrong, please try again later');
                }
            }
        })
        .finally(() => {
            loading.value = false;
        });
};

onMounted(() => {
    window.addEventListener('load', () => {
        if (!loggedIn.value) {
            grecaptcha.render('captcha', {
                sitekey: props.siteKey,
                callback: updateCaptcha
            });
        }
    });
});
</script>

<style lang="scss">
#contactForm {
    .form-input {
        margin-bottom: 0.3rem !important;
    }
    #captcha > div {
        width: 1px !important;
    }
    @media (max-width: 769px) {
        #captcha {
            transition: linear 100ms width;
            transform: scale(0.8);
            transform-origin: center left;
        }
    }
    @media (max-width: 319px) {
        #captcha {
            transform: scale(0.6);
        }
    }
}
</style>
